import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import PasswordField from '../components/common/PasswordField';
import refereeService from '../services/refereeService';
import { RefereeRegistrationInfo, CompleteRefereeRegistrationRequest } from '../types/referee';
import { useNotification } from '../hooks/useNotification';
import NotificationContainer from '../components/common/NotificationContainer';
import AnimatedBackground from '../components/common/AnimatedBackground';

const Background = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0a0f0a 0%, #1a2a1a 100%);
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
  z-index: 2;
`;

const FormCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 12px;
  padding: 40px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  backdrop-filter: none;

  @media (max-width: 768px) {
    padding: 30px 20px;
    border-radius: 15px;
  }
`;

const Title = styled.h1`
  color: #00d4ff;
  font-size: 2.5rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 10px;
  text-transform: uppercase;
  font-family: 'Courier New', monospace;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.h2`
  color: #88cc88;
  font-size: 1.2rem;
  text-align: center;
  margin-bottom: 30px;
  font-weight: 500;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: #b0b0b0;
  font-size: 0.9rem;
  font-weight: 500;
`;

const Input = styled.input`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #00d4ff;
    background: rgba(255, 255, 255, 0.15);
    box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.1);
  }

  &:disabled {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
    color: #666;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #666;
  }
`;

const Button = styled.button`
  background: linear-gradient(135deg, #4caf50, #45a049);
  border: none;
  color: white;
  padding: 15px 30px;
  border-radius: 8px;
  font-weight: bold;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #45a049, #3e8e41);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &:active {
    transform: translateY(0);
  }
`;

const InfoCard = styled.div`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
`;

const InfoTitle = styled.h3`
  color: #00d4ff;
  margin: 0 0 15px 0;
  font-size: 1.2rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: #b0b0b0;
  font-size: 0.9rem;
`;

const InfoValue = styled.span`
  color: white;
  font-weight: bold;
  text-align: right;
`;

const StatusMessage = styled.div<{ type: 'error' | 'warning' | 'info' }>`
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  font-weight: 500;
  
  ${props => {
    switch (props.type) {
      case 'error':
        return `
          background: rgba(244, 67, 54, 0.1);
          border: 1px solid rgba(244, 67, 54, 0.3);
          color: #f44336;
        `;
      case 'warning':
        return `
          background: rgba(255, 193, 7, 0.1);
          border: 1px solid rgba(255, 193, 7, 0.3);
          color: #ffc107;
        `;
      case 'info':
        return `
          background: rgba(0, 212, 255, 0.1);
          border: 1px solid rgba(0, 212, 255, 0.3);
          color: #00d4ff;
        `;
    }
  }}
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 3px solid rgba(0, 212, 255, 0.3);
    border-radius: 50%;
    border-top-color: #00d4ff;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const GameTypeBadge = styled.span`
  background: rgba(0, 212, 255, 0.2);
  color: #00d4ff;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
  text-transform: uppercase;
  margin-right: 8px;
  margin-bottom: 4px;
  display: inline-block;
`;

const RefereeRegistrationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { notifications, showNotification, removeNotification } = useNotification();
  
  const [loading, setLoading] = useState(true);
  const [registrationInfo, setRegistrationInfo] = useState<RefereeRegistrationInfo | null>(null);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [passwordValid, setPasswordValid] = useState(false);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      showNotification('error', 'Error', 'Registration token is missing');
      navigate('/');
      return;
    }

    loadRegistrationInfo();
  }, [token]);

  const loadRegistrationInfo = async () => {
    try {
      setLoading(true);
      const info = await refereeService.getRegistrationInfo(token!);
      setRegistrationInfo(info);
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to load registration information');
      setTimeout(() => navigate('/'), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = (): boolean => passwordValid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      showNotification('error', 'Invalid Password', 'Password does not meet requirements or passwords do not match');
      return;
    }

    try {
      setSubmitting(true);
      
      const request: CompleteRefereeRegistrationRequest = {
        token: token!,
        password: formData.password,
        confirmPassword: formData.confirmPassword
      };

      await refereeService.completeRegistration(request);
      
      // Показываем success страницу
      navigate('/referee-registration-success');
      
    } catch (error: any) {
      showNotification('error', 'Registration Failed', error.message || 'Failed to complete registration');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStatusMessage = () => {
    if (!registrationInfo) return null;

    switch (registrationInfo.status) {
      case 'expired':
        return (
          <StatusMessage type="error">
            Registration link has expired. Please contact the field to request a new invitation.
          </StatusMessage>
        );
      
      case 'has_active_field':
        return (
          <StatusMessage type="warning">
            You are already working at another field. You cannot register for multiple fields simultaneously.
          </StatusMessage>
        );
      
      case 'already_registered':
        return (
          <StatusMessage type="info">
            You are already registered for {registrationInfo.fieldName}. No further action is required.
          </StatusMessage>
        );
      
      case 'invitation_revoked':
        return (
          <StatusMessage type="error">
            The invitation from {registrationInfo.fieldName} has been revoked. Please contact the field for more information.
          </StatusMessage>
        );
      
      case 'field_not_found':
      case 'no_invitation':
      case 'unknown_status':
        return (
          <StatusMessage type="error">
            Invalid registration link. Please contact the field for assistance.
          </StatusMessage>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Background>
        <AnimatedBackground />
        <Container>
          <FormCard>
            <LoadingSpinner />
          </FormCard>
        </Container>
      </Background>
    );
  }

  if (!registrationInfo || !registrationInfo.canRegister) {
    return (
      <Background>
        <AnimatedBackground />
        <Container>
          <FormCard>
            <Title>Referee Registration</Title>
            {renderStatusMessage()}
          </FormCard>
        </Container>
        <NotificationContainer 
          notifications={notifications} 
          onRemove={removeNotification} 
        />
      </Background>
    );
  }

  return (
    <Background>
      <AnimatedBackground />
      <Container>
        <FormCard>
          <Title>Referee Registration</Title>
          <Subtitle>Complete Your Registration</Subtitle>

          <InfoCard>
            <InfoTitle>Invitation Details</InfoTitle>
            <InfoRow>
              <InfoLabel>Name:</InfoLabel>
              <InfoValue>{registrationInfo.firstName} {registrationInfo.lastName}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Email:</InfoLabel>
              <InfoValue>{registrationInfo.email}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Phone:</InfoLabel>
              <InfoValue>{registrationInfo.phone}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Field:</InfoLabel>
              <InfoValue>{registrationInfo.fieldName}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Role:</InfoLabel>
              <InfoValue>{registrationInfo.isMainReferee ? 'Main Referee' : 'Referee'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Game Types:</InfoLabel>
              <InfoValue>
                {registrationInfo.gameTypes?.map(type => (
                  <GameTypeBadge key={type}>{type}</GameTypeBadge>
                ))}
              </InfoValue>
            </InfoRow>
          </InfoCard>

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <PasswordField
                label="Password *"
                value={formData.password}
                onChange={(v)=>handleInputChange({target:{name:'password',value:v}} as any)}
                withConfirm
                confirmValue={formData.confirmPassword}
                onConfirmChange={(v)=>handleInputChange({target:{name:'confirmPassword',value:v}} as any)}
                autoComplete="new-password"
                onValidityChange={(ok)=>setPasswordValid(ok)}
                showInlineChecklist={false}
                showHelp
              />
            </FormGroup>

            <Button type="submit" disabled={submitting}>
              {submitting ? 'Registering...' : 'Complete Registration'}
            </Button>
          </Form>
        </FormCard>
      </Container>

      <NotificationContainer 
        notifications={notifications} 
        onRemove={removeNotification} 
      />
    </Background>
  );
};

export default RefereeRegistrationPage;